import React, { useState } from "react";
import { useIntl } from "react-intl";
import { Popover, PopoverBody } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faImages,
  faWandMagicSparkles,
  faMusic,
  faCrown,
  faSearch,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";

export interface ToolsConfig {
  stock: boolean;
  aiImage: boolean;
  music: boolean;
  istock: boolean;
  deepResearch: boolean;
  webSearch: boolean;
}

const TOOL_ITEMS: { key: keyof ToolsConfig; icon: typeof faImages; labelId: string }[] = [
  { key: "stock", icon: faImages, labelId: "app.videoGen.tools.stock" },
  { key: "aiImage", icon: faWandMagicSparkles, labelId: "app.videoGen.tools.aiImage" },
  { key: "music", icon: faMusic, labelId: "app.videoGen.tools.music" },
  { key: "istock", icon: faCrown, labelId: "app.videoGen.tools.istock" },
  { key: "deepResearch", icon: faCircleInfo, labelId: "app.videoGen.tools.deepResearch" },
  { key: "webSearch", icon: faSearch, labelId: "app.videoGen.tools.webSearch" },
];

interface ToolsPopoverProps {
  id: string;
  target?: string | HTMLElement | React.RefObject<HTMLElement> | null;
  isOpen: boolean;
  toggle: () => void;
  config: ToolsConfig;
  onChange: (config: ToolsConfig) => void;
}

export function ToolsPopover({
  id,
  target,
  isOpen,
  toggle,
  config,
  onChange,
}: ToolsPopoverProps) {
  const intl = useIntl();

  const handleToggle = (key: keyof ToolsConfig) => {
    onChange({ ...config, [key]: !config[key] });
  };

  if (!target) return null;

  return (
    <Popover placement="bottom" isOpen={isOpen} target={target} toggle={toggle}>
      <PopoverBody className="video-gen-tools-popover">
        <h6 className="mb-2">
          {intl.formatMessage({ id: "app.videoGen.toolsTitle" })}
        </h6>
        <p className="small text-muted mb-3">
          {intl.formatMessage({ id: "app.videoGen.toolsDescription" })}
        </p>
        <div className="video-gen-tools-popover__list">
          {TOOL_ITEMS.map(({ key, icon, labelId }) => (
            <div
              key={key}
              className="video-gen-tools-popover__row d-flex align-items-center justify-content-between py-2"
            >
              <span>
                <FontAwesomeIcon icon={icon} className="mr-2 text-muted" />
                {intl.formatMessage({ id: labelId })}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={config[key]}
                className={config[key] ? "video-gen-tools-popover__switch video-gen-tools-popover__switch--on" : "video-gen-tools-popover__switch"}
                onClick={() => handleToggle(key)}
              >
                <span className="video-gen-tools-popover__switch-dot" />
              </button>
            </div>
          ))}
        </div>
        <button type="button" className="btn btn-link btn-sm p-0 mt-2">
          {intl.formatMessage({ id: "app.videoGen.requestNewTool" })}
        </button>
      </PopoverBody>
    </Popover>
  );
}

export const DEFAULT_TOOLS: ToolsConfig = {
  stock: true,
  aiImage: false,
  music: true,
  istock: false,
  deepResearch: false,
  webSearch: true,
};
